import React from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Thin banner pinned at the top of the tenant Layout when the logged-in
 * user's tenant is a marketing sandbox (is_sandbox=true on user record).
 * Shows remaining TTL (approx) and a CTA to upgrade to a real trial.
 */
export default function SandboxBanner() {
  const { user } = useAuth();
  const [closed, setClosed] = React.useState(false);
  const [expiresIso, setExpiresIso] = React.useState(null);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    let cancelled = false;
    if (!user?.is_sandbox) return undefined;
    // Fetch tenant record to get sandbox_expires_at
    import("../lib/api").then(({ default: API }) => {
      API.get("/tenant/me")
        .then(({ data }) => {
          if (!cancelled) setExpiresIso(data?.sandbox_expires_at || null);
        })
        .catch(() => {});
    });
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.is_sandbox]);

  if (!user?.is_sandbox || closed) return null;

  let ttlText = "24h sandbox";
  if (expiresIso) {
    const diffMs = Date.parse(expiresIso) - now;
    if (!Number.isNaN(diffMs) && diffMs > 0) {
      const h = Math.floor(diffMs / 3_600_000);
      const m = Math.floor((diffMs % 3_600_000) / 60_000);
      ttlText = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
    } else if (!Number.isNaN(diffMs)) {
      ttlText = "Expired";
    }
  }

  return (
    <div
      className="w-full flex items-center justify-between gap-3 px-4 py-2 border-b text-sm"
      style={{
        background: "linear-gradient(90deg, rgba(19,93,212,0.16), rgba(32,194,252,0.08))",
        borderColor: "rgba(32,194,252,0.25)",
        color: "var(--foreground, #0F172A)",
      }}
      data-testid="sandbox-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
          style={{ background: "rgba(19,93,212,0.18)", color: "#135DD4" }}
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
          Sandbox
        </span>
        <span className="truncate">
          You're exploring Fixlr with demo data · <span className="font-semibold">{ttlText}</span>
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href="/signup"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold"
          style={{ background: "#135DD4", color: "#fff" }}
          data-testid="sandbox-upgrade"
        >
          Start a real trial →
        </a>
        <button
          type="button"
          aria-label="Dismiss banner"
          onClick={() => setClosed(true)}
          className="text-xs opacity-60 hover:opacity-100 px-2"
          data-testid="sandbox-banner-close"
        >
          ×
        </button>
      </div>
    </div>
  );
}
